import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useScrollContext } from '../contexts/ScrollContext';
import { USER } from '../utils/constants';

import TextInput from '../../../shared/FormStyles/TextInput';
import { scrollToView } from '../../campaigns/utils/scrollTo';

export const MessageInput = ({
  botIsTyping,
  processMessage,
  subscription,
  conversation,
}) => {
  const { t } = useTranslation('hrAssistants');
  const { chatBoxRef } = useScrollContext();
  const [message, setMessage] = useState('');

  /* eslint-disable no-shadow */
  const sendMessage = useCallback(
    message => {
      setMessage('');
      subscription.send({ message, conversation_id: conversation.id });
      processMessage({
        owner: USER,
        text: message,
        waitingBot: true,
      });
    },
    [setMessage, subscription, processMessage, conversation.id],
  );
  /* eslint-enable no-shadow */

  const disableSubmit = () => {
    if (botIsTyping || conversation.waitingBot) return true;
    if (!message.trim().length) return true;

    return false;
  };

  const onEnterPress = e => {
    if (disableSubmit()) return;

    if (e.keyCode === 13 && e.shiftKey === false) {
      sendMessage(message);
    }
  };

  const onClick = () => {
    if (disableSubmit()) return;

    sendMessage(message);
  };

  useEffect(() => {
    scrollToView(chatBoxRef.current.lastElementChild);
  }, [chatBoxRef]);

  return (
    <Container>
      <InputContainer>
        <TextInput
          height="60px"
          as="textarea"
          disabledVariant="whiteBg"
          placeholder={t('input.placeholder')}
          hasIcon={true}
          onChange={e => {
            setMessage(e.target.value);
          }}
          value={message}
          onKeyUp={onEnterPress}
        />
        <SendButtonContainer
          data-testid="hr-assistant-send-btn"
          $waitingBot={conversation.waitingBot}
          $disabled={disableSubmit()}
          onClick={onClick}
        >
          <SendIcon />
        </SendButtonContainer>
      </InputContainer>
    </Container>
  );
};

MessageInput.propTypes = {
  waitingBot: PropTypes.object,
  botIsTyping: PropTypes.bool,
  processMessage: PropTypes.func,
  subscription: PropTypes.object,
  conversation: PropTypes.object,
};

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  margin-top: auto;
`;

const SendButtonContainer = styled.div`
  cursor: pointer;
  position: absolute;
  right: 14px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => {
    if (props.$disabled) return '#A0A4A8';
    return '#007EFF';
  }};
  border: none;
  color: #ffffff;
  border-radius: 8px;
  bottom: 10px;
  opacity: ${props => (props.$waitingBot ? 0.5 : 1)};
`;

const InputContainer = styled.div`
  position: relative;
`;

const SendIcon = styled.i.attrs({
  className: 'fa-regular fa-paper-plane-top',
})`
  transform: rotate(315deg);
`;
